import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { ApiResponseInterface, IFieldRequest } from '../../shared/interfaces/common.interface';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  private projectsSubject = new BehaviorSubject<any[]>([]);
  public projects$ = this.projectsSubject.asObservable();

  constructor(
    private api: ApiService,
    private LocalStorageService: LocalStorageService,
    private http:HttpClient
  ) { }

  /**
   * Fetches the list of projects from the API and updates the projectsSubject.
   *
   * - On success: Emits the retrieved project list.
   * - On error: Emits an empty array.
   */
  listProjects(page?: number, limit?: number, project?: string) {
    this.api.GET(`projects${page ? '?page=' + page : ''}${limit ? '&limit=' + limit : ''}${project ? '&project=' + project : ''}`).subscribe({
      next: (responce: any) => {
        this.projectsSubject.next(responce.data.docs);
      },
      error: (err) => {
        this.projectsSubject.next([]);
      }
    })
  }
  /**
   * Gets the active project details from the API based on the URL.
   *
   * The active project ID is derived from the URL by splitting the URL
   * by '/' and taking the second to last element.
   *
   * @returns Observable<ApiResponseInterface> - The API response containing the project details.
   */
  getActiveProject() {
    const id = window.location.href.split('/')[window.location.href.split('/').length - 2];
    return this.api.GET('projects/' + id);
  }

  /**
   * Sends a request to invite a user to the project.
   *
   * - The project ID is derived from the URL by splitting the URL
   *   by '/' and taking the second to last element.
   *
   * @param {any} data The request data containing the user's name and email.
   *
   * @returns Observable<ApiResponseInterface> - The API response containing the result of the invitation.
   */
  inviteUserToProject(data: any) {
    data.project_id = window.location.href.split('/')[window.location.href.split('/').length - 2];
    return this.api.POST('projects/invite', data);
  }

  /**
   * Retrieves a list of users associated with the active project.
   *
   * The project ID is extracted from the current URL by splitting it
   * and accessing the second to last segment.
   *
   * @returns Observable<ApiResponseInterface> - The API response containing the list of project users.
   */
  getProjectUsers() {
    const id = window.location.href.split('/')[window.location.href.split('/').length - 2];
    const url = `projects/user/${id}?page=1&limit=25`;
    return this.api.GET(url);
  }

  /**
   * Deletes the active project based on its ID extracted from the URL.
   *
   * The project ID is derived from the URL by splitting it and taking
   * the second to last segment.
   *
   * @returns Observable<any> - The response from the API.
   */
  deleteProject() {
    const id = window.location.href.split('/')[window.location.href.split('/').length - 2];
    const url = `projects/${id}?page=1&limit=25`;
    return this.api.DELETE(url);
  }

  /**
 * Retrieves client exceptions for a specific project.
 *
 * The project ID is obtained from the local storage. This function fetches
 * client error data from the API for a given project, based on pagination,
 * error search query, and a specified time range in days.
 *
 * @param {number} page - The page number for pagination.
 * @param {number} limit - The maximum number of records to fetch per page.
 * @param {string} errors - The search query for filtering errors.
 * @param {number} days - The number of days to look back for errors.
 * @returns {Observable<ApiResponseInterface>} - The API response containing client exceptions data.
 */
  getClintExceptions(page: number, limit: number, errors: string, days: number) {
    const projectId = this.LocalStorageService.get('projectId');
    return this.api.GET(`client/errors/project/${projectId}?page=${page}&limit=${limit}&errors=${errors}&days=${days}`);
  }

  /**
 * Retrieves API exceptions for a specific project.
 *
 * The project ID is obtained from the local storage. This function fetches
 * API error data from the API for a given project, based on pagination,
 * error search query, and a specified time range in days.
 *
 * @param {number} page - The page number for pagination.
 * @param {number} limit - The maximum number of records to fetch per page.
 * @param {string} errors - The search query for filtering errors.
 * @param {number} days - The number of days to look back for errors.
 * @returns {Observable<ApiResponseInterface>} - The API response containing API exceptions data.
 */
  getApiExceptions(page: number, limit: number, errors: string, days: number) {
    const projectId = this.LocalStorageService.get('projectId');
    return this.api.GET(`api/errors/project/${projectId}?page=${page}&limit=${limit}&errors=${errors}&days=${days}`);
  }

  /**
   * Gets analytics
   * @param {number} page
   * @param {number} limit
   * @returns
   */
  getAnalytics(page: number, limit: number, analytics: string, days: number) {
    const projectId = this.LocalStorageService.get('projectId');
    return this.api.GET(`analytics/client/project/${projectId}?page=${page}&limit=${limit}&analytics=${analytics}&days=${days}`);
  }

  /**
   * Gets api logs details
   * @param {number } page
   * @param {number } limit
   * @returns
   */
  getApiLogsDetails(page: number, limit: number, logs: string, days: number) {
    const projectId = this.LocalStorageService.get('projectId');
    return this.api.GET(`api/logs/project/${projectId}?page=${page}&limit=${limit}&logs=${logs}&days=${days}`)
  }

  /**
   * Gets client logs details
   * @param {number} page
   * @param {number} limit
   * @returns
   */
  getClientLogsDetails(page: number, limit: number, logs: string, days: number) {
    const projectId = this.LocalStorageService.get('projectId');
    return this.api.GET(`client/logs/project/${projectId}?page=${page}&limit=${limit}&logs=${logs}&days=${days}`)
  }

  /**
   * Creates project
   * @param catogryData
   * @returns
   */
  createProject(catogryData: any) {
    return this.api.POST('projects', catogryData);
  }

  /**
   * Gets project by id
   * @param projectId
   * @returns project by id
   */
  getProjectById(projectId: string): Observable<ApiResponseInterface> {
    const project = this.LocalStorageService.get('projectId');
    // console.log("PROJECTID", projectId);

    return this.api.GET(`projects/${project}`);
  }

  /**
   * Updates field by id
   * @param payload
   * @returns field by id
   */
  updateFieldById(payload: IFieldRequest): Observable<ApiResponseInterface> {
    const projectId = this.LocalStorageService.get('projectId');
    return this.api.PATCH(`projects/${projectId}`, payload);
  }


  /**
   * Determines whether search on
   * @param event
   * @param searchCallback
   */
  onSearch(event: any, searchCallback: (searchTerm: string) => void) {
    const searchTerm = event.target.value.trim();
    searchCallback(searchTerm);
  }

  /**
   *
   * @param userId The ID of the user to be removed from the project.
   * @returns Observable<any> - The response from the API.
   */
  deleteUserFromProject(userId: string): Observable<any> {
    // Get the project ID from the current URL
    const projectId = window.location.href.split('/')[window.location.href.split('/').length - 2];
    // Build the API URL with the projectId and userId
    const url = `projects/${projectId}/users/${userId}`;
    return this.api.DELETE(url);
  }
  
  /**
   * Gets accept user status
   * @param projectId 
   * @param userId 
   * @param status 
   * @returns accept user status 
   */
  getAcceptUserStatus(projectId: string, userId: string, status: number): Observable<ApiResponseInterface> {
    const url = `projects/join?project_id=${projectId}&user_id=${userId}&status=${status}`;
    return this.api.GET(url);
  } 
  /**
   * Gets reject user status
   * @param projectId 
   * @param userId 
   * @param status 
   * @returns reject user status 
   */
  getRejectUserStatus(projectId: string, userId: string, status: number): Observable<ApiResponseInterface> {
    const url = `projects/reject?project_id=${projectId}&user_id=${userId}&status=${status}`;
    return this.api.GET(url);
  }
}
